// Wall-clock accounting drifts from the audio it describes: it cannot know
// about segments recorded in an earlier page load, and it counts time the
// encoder may not have captured. Decoding the blob is the only way to learn
// how long a recording actually is.
export interface MeasureAudioDurationOptions {
  audioContextFactory?: () => BaseAudioContext;
}

const createDefaultAudioContext = (): BaseAudioContext => {
  const AudioContextConstructor = window.AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextConstructor) {
    throw new Error('AudioContext is not available for measuring audio');
  }

  return new AudioContextConstructor();
};

const decodeAudioData = (
  audioContext: BaseAudioContext,
  arrayBuffer: ArrayBuffer
): Promise<AudioBuffer> => new Promise((resolve, reject) => {
  const maybePromise = audioContext.decodeAudioData(arrayBuffer, resolve, reject);
  if (maybePromise && typeof maybePromise.then === 'function') {
    maybePromise.then(resolve, reject);
  }
});

/**
 * Length of an audio blob in milliseconds, or null when it cannot be decoded.
 * Callers must treat null as "unknown" and keep their existing estimate.
 */
export const measureAudioDurationMs = async (
  blob: Blob | null | undefined,
  options: MeasureAudioDurationOptions = {}
): Promise<number | null> => {
  if (!blob || blob.size === 0) {
    return null;
  }

  let audioContext: BaseAudioContext | null = null;
  try {
    audioContext = (options.audioContextFactory ?? createDefaultAudioContext)();
    const decoded = await decodeAudioData(audioContext, await blob.arrayBuffer());
    return decoded.duration * 1000;
  } catch (error) {
    console.warn('Could not measure recording duration:', error);
    return null;
  } finally {
    await (audioContext as { close?: () => Promise<void> } | null)?.close?.();
  }
};
